import { photoSrc } from '../lib/photoSrc';
import type {
  AgentActionLog,
  AgentKey,
  AgentToolRegistry,
  BrainDoc,
  BrainRevision,
  Capability,
  Client,
  KillSwitchState,
  Me,
  MemberPermissions,
  Milestone,
  MintedKey,
  OpsUser,
  Paginated,
  PortalProjectSummary,
  Project,
  CreateDailyLogBody,
  DailyLog,
  DailyLogPhoto,
  ProjectDashboard,
  ProjectDeliveryMilestone,
  ProjectFile,
  ProjectFileCategory,
  ProjectNote,
  ProjectNoteCategory,
  ProjectHoliday,
  ProjectSchedule,
  ProjectShare,
  ProjectSnapshot,
  ProjectTrack,
  ScheduleDependency,
  ScheduleNodeKind,
  QualificationChecklist,
  SessionSummary,
  TrackItem,
  TrackItemDependency,
  TrackTypeDef,
  UpsertBrainDoc,
  ValidationMetrics,
  // Finance epic (2026-07)
  ApprovalRequest,
  Budget,
  BudgetStatusRow,
  BalanceSheet,
  BupotIssued,
  BupotReceived,
  CashFlow,
  ComplianceCalendar,
  CompliancePackage,
  DpbReport,
  EmploymentContract,
  Expense,
  ExpiryReminder,
  FinalPphWindow,
  LeaveBalanceView,
  LeaveRequest,
  PayrollExport,
  PayrollPreview,
  PayrollProvider,
  Invoice,
  JournalEntry,
  LedgerAccount,
  MissingReport,
  MonthAllocations,
  Payment,
  Person,
  ProjectPnl,
  PettyCashBalance,
  PettyCashTopup,
  PphBadanEstimate,
  PpnWorksheet,
  ProfitAndLoss,
  Quotation,
  ReconcileResult,
  SalaryRecord,
  SettlementReport,
  TaxPayment,
  TaxPrep,
  TaxType,
  Vendor,
  VendorPayment,
} from './types';
import type { ClientShareView, UpdatesPage } from './publicShare';

export class OpsApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly serverMessage: string | null = null,
    /** Structured agent-envelope code (e.g. VALIDATION_FAILED) when present. */
    public readonly errorCode: string | null = null,
  ) {
    super(message);
    this.name = 'OpsApiError';
  }
}

type TokenProvider = () => string | null;
let _tokenProvider: TokenProvider = () => null;
export function setApiTokenProvider(provider: TokenProvider): void {
  _tokenProvider = provider;
}

export interface ApiAuthHandlers {
  getToken: TokenProvider;
  /** Exchange the refresh token for new tokens; resolve the new access token (null = failed). */
  refresh: () => Promise<string | null>;
  /** Called when a 401 cannot be recovered (refresh failed) — logout + redirect. */
  onAuthFailure: () => void;
}

let _authHandlers: ApiAuthHandlers | null = null;
export function setApiAuthHandlers(handlers: ApiAuthHandlers | null): void {
  _authHandlers = handlers;
  if (handlers) _tokenProvider = handlers.getToken;
}

/** Single-flight: concurrent 401s share one refresh round-trip. */
let _refreshInFlight: Promise<string | null> | null = null;
function refreshOnce(): Promise<string | null> {
  if (!_authHandlers) return Promise.resolve(null);
  _refreshInFlight ??= _authHandlers
    .refresh()
    .catch(() => null)
    .finally(() => {
      _refreshInFlight = null;
    });
  return _refreshInFlight;
}

/**
 * Fetch with the bearer token; on a 401 (outside /api/auth/*) refresh once and
 * retry. A failed refresh surfaces onAuthFailure (logout + /login redirect).
 */
async function authedFetch(path: string, init: RequestInit): Promise<Response> {
  const headers = (token: string | null): HeadersInit => ({
    ...(init.headers as Record<string, string>),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  });
  const res = await fetch(path, { ...init, headers: headers(_tokenProvider()) });
  if (res.status !== 401 || path.startsWith('/api/auth/')) return res;
  const newToken = await refreshOnce();
  if (!newToken) {
    _authHandlers?.onAuthFailure();
    return res;
  }
  return fetch(path, { ...init, headers: headers(newToken) });
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await authedFetch(path, {
    method,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let serverMessage: string | null = null;
    try {
      const json = (await res.json()) as { message?: string | string[] };
      serverMessage = Array.isArray(json.message)
        ? json.message.join(', ')
        : (json.message ?? null);
    } catch {
      /* no JSON body */
    }
    throw new OpsApiError(
      `${method} ${path} → ${res.status}${serverMessage ? `: ${serverMessage}` : ''}`,
      res.status,
      serverMessage,
    );
  }
  // 204/205 carry no body by definition; a void handler can also reply with an
  // empty 200. Read the body as text first so an empty response resolves to
  // undefined instead of throwing "Unexpected end of JSON input" on res.json().
  if (res.status === 204 || res.status === 205) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** Multipart upload (POST/PUT) with the same auth + retry as `request`. */
async function requestMultipart<T>(path: string, form: FormData, method: 'POST' | 'PUT' = 'POST'): Promise<T> {
  const res = await authedFetch(path, {
    method,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      // No Content-Type: the browser sets the multipart boundary itself.
    },
    body: form,
  });
  if (!res.ok) {
    let serverMessage: string | null = null;
    try {
      const json = (await res.json()) as { message?: string | string[] };
      serverMessage = Array.isArray(json.message) ? json.message.join(', ') : (json.message ?? null);
    } catch {
      /* no JSON body */
    }
    throw new OpsApiError(
      `${method} ${path} → ${res.status}${serverMessage ? `: ${serverMessage}` : ''}`,
      res.status,
      serverMessage,
    );
  }
  return (await res.json()) as T;
}

/** Fetch a binary response (e.g. a PDF) with the same auth + retry as `request`. */
async function requestBlob(path: string): Promise<Blob> {
  const res = await authedFetch(path, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {},
  });
  if (!res.ok) {
    throw new OpsApiError(`GET ${path} → ${res.status}`, res.status, null);
  }
  return res.blob();
}

const qs = (params: Record<string, unknown>): string => {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  return entries.length
    ? '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&')
    : '';
};

/** The uniform agent-result envelope the brain endpoints return at HTTP 200. */
interface Envelope<T> {
  ok: boolean;
  data?: T;
  message?: string;
  errorCode?: string;
}

/**
 * Unwrap an `{ok,data}` envelope, throwing an {@link OpsApiError} on `ok:false`
 * (which arrives as HTTP 200 from the brain endpoints). The errorCode is
 * surfaced so callers (e.g. the brain secret-scan reject) can branch on it.
 */
async function unwrap<T>(p: Promise<Envelope<T>>): Promise<T> {
  const env = await p;
  if (!env.ok) {
    throw new OpsApiError(
      env.message ?? 'request failed',
      200,
      env.message ?? null,
      env.errorCode ?? null,
    );
  }
  return env.data as T;
}

export const opsApi = {
  // ── Auth: self-service password reset (public; no token required) ──
  forgotPassword: (email: string) =>
    request<{ message: string }>('POST', '/api/auth/forgot-password', { email }),
  resetPassword: (token: string, newPassword: string) =>
    request<{ message: string }>('POST', '/api/auth/reset-password', { token, newPassword }),

  // ── Clients (contact store; create-project needs the list) ──
  listClients: (q?: string) => request<Client[]>('GET', `/api/clients${qs({ q })}`),
  createClient: (body: Partial<Client>) => request<Client>('POST', '/api/clients', body),
  updateClient: (id: string, body: Partial<Client>) =>
    request<Client>('PATCH', `/api/clients/${id}`, body),
  deleteClient: (id: string) => request<void>('DELETE', `/api/clients/${id}`),

  // ── Projects ──
  listProjects: () => request<Project[]>('GET', '/api/projects'),
  createProject: (body: unknown) => request<Project>('POST', '/api/projects', body),
  updateProjectStatus: (id: string, status: string) =>
    request<Project>('PATCH', `/api/projects/${id}/status`, { status }),
  updateMilestone: (id: string, body: unknown) =>
    request<Milestone>('PATCH', `/api/milestones/${id}`, body),

  // Projects: client share link (read-only progress dashboard)
  getProjectShare: (id: string) =>
    request<ProjectShare | null>('GET', `/api/projects/${id}/share`),
  enableProjectShare: (id: string) =>
    request<ProjectShare>('POST', `/api/projects/${id}/share`),
  revokeProjectShare: (id: string) =>
    request<void>('DELETE', `/api/projects/${id}/share`),

  // ── Project files (per-project document library) ──
  listProjectFiles: (projectId: string) =>
    request<ProjectFile[]>('GET', `/api/projects/${projectId}/files`),
  uploadProjectFile: (
    projectId: string,
    file: File,
    meta?: { category?: ProjectFileCategory; name?: string; description?: string; visibleToClient?: boolean },
  ) => {
    const form = new FormData();
    form.append('file', file);
    if (meta?.category) form.append('category', meta.category);
    if (meta?.name?.trim()) form.append('name', meta.name.trim());
    if (meta?.description?.trim()) form.append('description', meta.description.trim());
    if (meta?.visibleToClient !== undefined) form.append('visibleToClient', String(meta.visibleToClient));
    return requestMultipart<ProjectFile>(`/api/projects/${projectId}/files`, form);
  },
  updateProjectFile: (
    fileId: string,
    body: Partial<{ category: ProjectFileCategory; name: string; description: string | null; visibleToClient: boolean }>,
  ) => request<ProjectFile>('PATCH', `/api/project-files/${fileId}`, body),
  replaceProjectFile: (fileId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return requestMultipart<ProjectFile>(`/api/project-files/${fileId}/file`, form, 'PUT');
  },
  deleteProjectFile: (fileId: string) => request<void>('DELETE', `/api/project-files/${fileId}`),
  downloadProjectFile: (fileId: string) => requestBlob(`/api/project-files/${fileId}/download`),

  // ── Project notes (per-project "Daily Notes" — internal markdown journal) ──
  listProjectNotes: (projectId: string, category?: ProjectNoteCategory) =>
    request<ProjectNote[]>('GET', `/api/projects/${projectId}/notes${qs({ category })}`),
  createProjectNote: (
    projectId: string,
    body: { title: string; category?: ProjectNoteCategory; body?: string; noteDate?: string },
  ) => request<ProjectNote>('POST', `/api/projects/${projectId}/notes`, body),
  updateProjectNote: (
    noteId: string,
    body: Partial<{ title: string; category: ProjectNoteCategory; body: string; noteDate: string }>,
  ) => request<ProjectNote>('PATCH', `/api/project-notes/${noteId}`, body),
  deleteProjectNote: (noteId: string) => request<void>('DELETE', `/api/project-notes/${noteId}`),

  // ── Projects: execution tracker ──
  getProjectDashboard: (id: string) =>
    request<ProjectDashboard>('GET', `/api/projects/${id}/dashboard`),
  createDailyLog: (body: CreateDailyLogBody) =>
    request<DailyLog>('POST', '/api/daily-logs', body),
  updateDailyLog: (
    logId: string,
    body: Partial<{ reportedDate: string; progressIncrement: number; note: string; hasProblem: boolean }>,
  ) => request<DailyLog>('PATCH', `/api/daily-logs/${logId}`, body),
  uploadLogPhoto: (logId: string, file: File, caption?: string) => {
    const form = new FormData();
    form.append('file', file);
    if (caption?.trim()) form.append('caption', caption.trim());
    return requestMultipart<DailyLogPhoto>(`/api/daily-logs/${logId}/photos`, form);
  },
  updatePhotoCaption: (photoId: string, caption: string | null) =>
    request<DailyLogPhoto>('PATCH', `/api/daily-logs/photos/${photoId}`, { caption }),
  /** Photo bytes with the bearer token — /api/photos/* is auth-guarded, so a
   *  plain <img src> would 401. Callers turn the blob into an object URL. */
  photoBlob: (filePath: string) => requestBlob(photoSrc(filePath)),
  deleteLogPhoto: (photoId: string) =>
    request<void>('DELETE', `/api/daily-logs/photos/${photoId}`),
  listTracks: (projectId: string) =>
    request<ProjectTrack[]>('GET', `/api/project-tracks/project/${projectId}`),
  getTrack: (id: string) =>
    request<ProjectTrack>('GET', `/api/project-tracks/${id}`),
  listDailyLogs: (trackId: string, params?: { from?: string; to?: string }) =>
    request<DailyLog[]>('GET', `/api/daily-logs/track/${trackId}${qs(params ?? {})}`),
  listQualifications: (projectId: string) =>
    request<QualificationChecklist[]>('GET', `/api/qualifications/project/${projectId}`),
  getChecklist: (id: string) =>
    request<QualificationChecklist>('GET', `/api/qualifications/${id}`),
  exportChecklistPdf: (id: string) => requestBlob(`/api/qualifications/${id}/export.pdf`),
  getProjectReport: (projectId: string, params?: { from?: string; to?: string }) =>
    request<unknown>('GET', `/api/project-reports/${projectId}${qs(params ?? {})}`),
  createTrack: (body: unknown) => request<ProjectTrack>('POST', '/api/project-tracks', body),
  updateTrack: (id: string, body: unknown) =>
    request<ProjectTrack>('PATCH', `/api/project-tracks/${id}`, body),
  addTrackItem: (trackId: string, body: unknown) => request<unknown>('POST', `/api/project-tracks/${trackId}/items`, body),
  deleteTrack: (id: string) => request<void>('DELETE', `/api/project-tracks/${id}`),

  // Sub-task progress: set a track item's % directly (the main task % rolls up from it)
  setItemProgress: (itemId: string, progressPct: number) =>
    request<TrackItem>('PATCH', `/api/project-tracks/items/${itemId}/progress`, { progressPct }),

  // Scheduled timeline: plan dates, dependencies, baseline
  updateItemPlan: (itemId: string, body: { plannedStart?: string | null; plannedEnd?: string | null }) =>
    request<TrackItem>('PATCH', `/api/project-tracks/items/${itemId}/plan`, body),
  listDependencies: (projectId: string) =>
    request<TrackItemDependency[]>('GET', `/api/project-tracks/project/${projectId}/dependencies`),
  getSchedule: (projectId: string) =>
    request<ProjectSchedule>('GET', `/api/project-tracks/project/${projectId}/schedule`),
  listHolidays: (projectId: string) =>
    request<ProjectHoliday[]>('GET', `/api/project-tracks/project/${projectId}/holidays`),
  addHoliday: (projectId: string, body: { holidayDate: string; label?: string | null }) =>
    request<ProjectHoliday>('POST', `/api/project-tracks/project/${projectId}/holidays`, body),
  removeHoliday: (id: string) =>
    request<void>('DELETE', `/api/project-tracks/holidays/${id}`),
  addDependency: (body: { predecessorId: string; successorId: string; lagDays?: number }) =>
    request<TrackItemDependency>('POST', '/api/project-tracks/dependencies', body),
  updateDependencyLag: (id: string, lagDays: number) =>
    request<TrackItemDependency>('PATCH', `/api/project-tracks/dependencies/${id}`, { lagDays }),
  removeDependency: (id: string) =>
    request<void>('DELETE', `/api/project-tracks/dependencies/${id}`),
  setBaseline: (projectId: string) =>
    request<{ projectId: string; itemsBaselined: number; rebaselined: boolean }>(
      'POST',
      `/api/project-tracks/project/${projectId}/baseline`,
    ),
  // ── Project backups (snapshots): save the full state, restore in place ──
  listSnapshots: (projectId: string) =>
    request<ProjectSnapshot[]>('GET', `/api/projects/${projectId}/snapshots`),
  createSnapshot: (projectId: string, name?: string) =>
    request<ProjectSnapshot>('POST', `/api/projects/${projectId}/snapshots`, { name }),
  restoreSnapshot: (projectId: string, snapshotId: string) =>
    request<{ restored: ProjectSnapshot['summary'] }>(
      'POST',
      `/api/projects/${projectId}/snapshots/${snapshotId}/restore`,
    ),
  deleteSnapshot: (projectId: string, snapshotId: string) =>
    request<void>('DELETE', `/api/projects/${projectId}/snapshots/${snapshotId}`),
  exportSnapshot: (projectId: string, snapshotId: string) =>
    requestBlob(`/api/projects/${projectId}/snapshots/${snapshotId}/export`),
  importSnapshot: (projectId: string, form: FormData) =>
    requestMultipart<ProjectSnapshot>(`/api/projects/${projectId}/snapshots/import`, form),

  createDeliveryMilestone: (body: unknown) =>
    request<ProjectDeliveryMilestone>('POST', '/api/project-delivery-milestones', body),
  updateDeliveryMilestone: (id: string, body: unknown) =>
    request<ProjectDeliveryMilestone>('PATCH', `/api/project-delivery-milestones/${id}`, body),
  deleteDeliveryMilestone: (id: string) => request<void>('DELETE', `/api/project-delivery-milestones/${id}`),

  // ── Milestone schedule links (polymorphic item ↔ milestone edges) ──
  listMilestoneLinks: (projectId: string) =>
    request<ScheduleDependency[]>('GET', `/api/project-delivery-milestones/project/${projectId}/links`),
  createMilestoneLink: (body: {
    predecessor: { kind: ScheduleNodeKind; id: string };
    successor: { kind: ScheduleNodeKind; id: string };
    lagDays?: number;
  }) => request<ScheduleDependency>('POST', '/api/project-delivery-milestones/links', body),
  updateMilestoneLinkLag: (id: string, lagDays: number) =>
    request<ScheduleDependency>('PATCH', `/api/project-delivery-milestones/links/${id}`, { lagDays }),
  removeMilestoneLink: (id: string) =>
    request<void>('DELETE', `/api/project-delivery-milestones/links/${id}`),
  createQualification: (body: unknown) => request<unknown>('POST', '/api/qualifications', body),
  addChecklistItem: (body: unknown) => request<unknown>('POST', '/api/qualifications/items', body),

  // ── Track-type catalog (Settings → Track Types) ──
  listTrackTypes: (includeInactive = false) =>
    request<TrackTypeDef[]>('GET', `/api/track-types${qs({ includeInactive: includeInactive || undefined })}`),
  createTrackType: (body: { label: string; code?: string; defaultUnitLabel?: string; sortOrder?: number }) =>
    request<TrackTypeDef>('POST', '/api/track-types', body),
  updateTrackType: (
    id: string,
    body: Partial<{ label: string; defaultUnitLabel: string; sortOrder: number; active: boolean }>,
  ) => request<TrackTypeDef>('PATCH', `/api/track-types/${id}`, body),
  reorderTrackTypes: (orderedIds: string[]) =>
    request<TrackTypeDef[]>('PATCH', '/api/track-types/reorder', { orderedIds }),

  // ── Client portal (role=client logins; scoped to their customer's projects) ──
  portalProjects: () => request<PortalProjectSummary[]>('GET', '/api/portal/projects'),
  portalProjectView: (id: string) =>
    request<ClientShareView>('GET', `/api/portal/projects/${id}`),
  portalUpdatesPage: (id: string, page: number, pageSize: number) =>
    request<UpdatesPage>('GET', `/api/portal/projects/${id}/updates${qs({ page, pageSize })}`),
  portalPhotoBlob: (projectId: string, photoId: string) =>
    requestBlob(`/api/portal/projects/${projectId}/photos/${photoId}`),
  portalFileBlob: (projectId: string, fileId: string) =>
    requestBlob(`/api/portal/projects/${projectId}/files/${fileId}`),

  // ── Agent: activity log + kill-switch ──
  agentLog: (params: Record<string, unknown> = {}) =>
    request<Paginated<AgentActionLog>>('GET', `/api/agent-activity${qs(params)}`),
  overrideAction: (id: string) =>
    request<AgentActionLog>('POST', `/api/agent-actions/${id}/override`),
  killSwitch: () => request<KillSwitchState>('GET', '/api/agents/kill-switch'),
  setKillSwitch: (halted: boolean, reason?: string) =>
    request<KillSwitchState>('POST', '/api/agents/kill-switch', { halted, reason }),

  // ── Agent: scoped keys ──
  listAgentKeys: () => request<AgentKey[]>('GET', '/api/agent-keys'),
  mintAgentKey: (body: { name: string; allowedTiers?: string[]; allowedTools?: string[] }) =>
    request<MintedKey>('POST', '/api/agent-keys', body),
  updateAgentKey: (
    id: string,
    body: { allowedTiers?: string[]; allowedTools?: string[]; enabled?: boolean },
  ) => request<AgentKey>('PATCH', `/api/agent-keys/${id}`, body),
  revokeAgentKey: (id: string) => request<AgentKey>('POST', `/api/agent-keys/${id}/revoke`),

  // ── Agent: sessions + validation metrics ──
  agentSessions: (limit = 50) =>
    request<SessionSummary[]>('GET', `/api/agent-activity/sessions${qs({ limit })}`),
  sessionTimeline: (agentId: string) =>
    request<AgentActionLog[]>('GET', `/api/agent-activity/sessions/${encodeURIComponent(agentId)}`),
  validationMetrics: () => request<ValidationMetrics>('GET', '/api/metrics/validation'),

  // The canonical tool registry (JWT-guarded mirror of /agent/tools).
  agentToolRegistry: () => request<AgentToolRegistry>('GET', '/api/agent-tools'),

  // ── Agent brain (founder-gated; endpoints return {ok,data} envelopes) ──
  brainDocs: () => unwrap<BrainDoc[]>(request('GET', '/api/brain/docs')),
  brainDoc: (kind: string, slug: string) =>
    unwrap<BrainDoc>(
      request('GET', `/api/brain/doc/${encodeURIComponent(kind)}/${encodeURIComponent(slug)}`),
    ),
  brainRevisions: (kind: string, slug: string) =>
    unwrap<BrainRevision[]>(
      request('GET', `/api/brain/doc/${encodeURIComponent(kind)}/${encodeURIComponent(slug)}/revisions`),
    ),
  brainUpsert: (body: UpsertBrainDoc) => unwrap<BrainDoc>(request('POST', '/api/brain/doc', body)),
  brainRetire: (kind: string, slug: string) =>
    unwrap<{ kind: string; slug: string; retired: boolean }>(
      request('DELETE', `/api/brain/doc/${encodeURIComponent(kind)}/${encodeURIComponent(slug)}`),
    ),
  brainRestore: (kind: string, slug: string) =>
    unwrap<BrainDoc>(
      request('POST', `/api/brain/doc/${encodeURIComponent(kind)}/${encodeURIComponent(slug)}/restore`),
    ),

  // ── Team identity + RBAC (user management) ──
  me: () => request<Me>('GET', '/api/team/me'),
  listMembers: () => request<OpsUser[]>('GET', '/api/team/members'),
  createMember: (body: {
    username: string;
    password: string;
    name: string;
    email?: string;
    role: string;
    clientId?: string | null;
    telegramUserId?: string;
  }) => request<OpsUser>('POST', '/api/team/members', body),
  updateMember: (
    id: string,
    body: Partial<{
      username: string;
      name: string;
      email: string;
      role: string;
      clientId: string | null;
      telegramUserId: string;
      active: boolean;
    }>,
  ) => request<OpsUser>('PATCH', `/api/team/members/${id}`, body),
  resetMemberPassword: (id: string, password: string) =>
    request<void>('POST', `/api/team/members/${id}/reset-password`, { password }),
  memberPermissions: (id: string) =>
    request<MemberPermissions>('GET', `/api/team/members/${id}/permissions`),
  updateMemberPermissions: (id: string, permissions: Record<string, boolean> | null) =>
    request<MemberPermissions>('PUT', `/api/team/members/${id}/permissions`, { permissions }),
  permissionDefaults: (role: string) =>
    request<Capability>('GET', `/api/team/permissions/defaults${qs({ role })}`),

  // ── Finance epic: expenses ──
  listExpenses: (params: Record<string, unknown> = {}) =>
    request<Expense[]>('GET', `/api/expenses${qs(params)}`),
  createExpense: (body: unknown) => request<Expense>('POST', '/api/expenses', body),
  updateExpense: (id: string, body: unknown) =>
    request<Expense>('PATCH', `/api/expenses/${id}`, body),
  deleteExpense: (id: string) => request<void>('DELETE', `/api/expenses/${id}`),

  // ── Finance epic: ledger + statements ──
  ledgerAccounts: () => request<LedgerAccount[]>('GET', '/api/ledger/accounts'),
  journal: (params: Record<string, unknown> = {}) =>
    request<JournalEntry[]>('GET', `/api/ledger/journal${qs(params)}`),
  postLedger: () => request<{ posted: number }>('POST', '/api/ledger/post'),
  pnl: (period: string) => request<ProfitAndLoss>('GET', `/api/statements/pnl${qs({ period })}`),
  balanceSheet: (period: string) =>
    request<BalanceSheet>('GET', `/api/statements/balance-sheet${qs({ period })}`),
  cashFlow: (period: string) =>
    request<CashFlow>('GET', `/api/statements/cash-flow${qs({ period })}`),

  // ── Finance epic: quotations ──
  listQuotations: () => request<Quotation[]>('GET', '/api/quotations'),
  createQuotation: (body: unknown) => request<Quotation>('POST', '/api/quotations', body),
  sendQuotation: (id: string) => request<Quotation>('POST', `/api/quotations/${id}/send`),
  acceptQuotation: (id: string) => request<Quotation>('POST', `/api/quotations/${id}/accept`),
  convertQuotation: (id: string, body: unknown) =>
    request<Project>('POST', `/api/quotations/${id}/convert`, body),

  // ── Finance epic: invoices ──
  listInvoices: () => request<Invoice[]>('GET', '/api/invoices'),
  createInvoice: (body: unknown) => request<Invoice>('POST', '/api/invoices', body),
  issueInvoice: (id: string) => request<Invoice>('POST', `/api/invoices/${id}/issue`),
  recordPayment: (id: string, body: unknown) =>
    request<Payment>('POST', `/api/invoices/${id}/payments`, body),
  invoicePdf: (id: string) => requestBlob(`/api/invoices/${id}/pdf`),

  // ── Finance epic: approval inbox ──
  listApprovals: (status?: string) =>
    request<ApprovalRequest[]>('GET', `/api/approvals${qs({ status })}`),
  approveRequest: (id: string, note?: string) =>
    request<ApprovalRequest>('POST', `/api/approvals/${id}/approve`, { note }),
  rejectRequest: (id: string, note: string) =>
    request<ApprovalRequest>('POST', `/api/approvals/${id}/reject`, { note }),

  // ── Finance epic: tax-prep ──
  ppnWorksheet: (period: string) =>
    request<PpnWorksheet>('GET', `/api/tax-prep/ppn${qs({ period })}`),
  pphBadan: (year: number) => request<PphBadanEstimate>('GET', `/api/tax-prep/pph-badan/${year}`),
  finalPphWindow: () => request<FinalPphWindow | null>('GET', '/api/tax-prep/final-pph-window'),
  setFinalPphWindow: (startYear: string) =>
    request<FinalPphWindow>('POST', '/api/tax-prep/final-pph-window', { startYear }),
  listTaxPreps: (period?: string) =>
    request<TaxPrep[]>('GET', `/api/tax-prep${qs({ period })}`),
  generateTaxPrep: (taxType: TaxType, period: string) =>
    request<TaxPrep>('POST', '/api/tax-prep', { taxType, period }),

  // ── Finance epic: compliance ──
  complianceCalendar: (year: number) =>
    request<ComplianceCalendar>('GET', `/api/compliance/calendar${qs({ year })}`),
  complianceMissing: (period: string) =>
    request<MissingReport>('GET', `/api/compliance/missing${qs({ period })}`),
  compliancePackages: () => request<CompliancePackage[]>('GET', '/api/compliance/packages'),
  assemblePackage: (period: string) =>
    request<CompliancePackage>('POST', '/api/compliance/packages', { period }),
  approvePackage: (id: string) =>
    request<CompliancePackage>('POST', `/api/compliance/packages/${id}/approve`),
  handoffPackage: (id: string, handedOffTo: string) =>
    request<CompliancePackage>('POST', `/api/compliance/packages/${id}/handoff`, { handedOffTo }),
  compliancePackagePdf: (id: string) =>
    requestBlob(`/api/compliance/packages/${id}/document`),

  // ── Finance epic: petty cash ──
  pettyCashTopups: () => request<PettyCashTopup[]>('GET', '/api/petty-cash/topups'),
  createPettyCashTopup: (body: unknown) =>
    request<PettyCashTopup>('POST', '/api/petty-cash/topups', body),
  pettyCashBalance: () => request<PettyCashBalance>('GET', '/api/petty-cash/balance'),
  pettyCashSettlement: (from: string, to: string) =>
    request<SettlementReport>('GET', `/api/petty-cash/settlement${qs({ from, to })}`),
  pettyCashSettlementPdf: (from: string, to: string) =>
    requestBlob(`/api/petty-cash/settlement.pdf${qs({ from, to })}`),
  pettyCashReconcile: (body: { countedAmount: number; countedAt?: string; notes?: string }) =>
    request<ReconcileResult>('POST', '/api/petty-cash/reconcile', body),
  /** Petty-cash voucher: fields + the REQUIRED receipt photo in ONE multipart request. */
  createPettyCashVoucher: (fields: Record<string, string | number>, receipt: File) => {
    const form = new FormData();
    for (const [k, v] of Object.entries(fields)) form.append(k, String(v));
    form.append('paidFrom', 'petty_cash');
    form.append('file', receipt);
    return requestMultipart<Expense>('/api/expenses', form);
  },

  // ── Finance epic: tax close (DPB / registers / runbook) ──
  peredaranBruto: (year: number) =>
    request<DpbReport>('GET', `/api/tax-prep/peredaran-bruto${qs({ year })}`),
  taxRunbookPdf: (masa: string) => requestBlob(`/api/tax-prep/runbook.pdf${qs({ masa })}`),
  listBupotReceived: (params: Record<string, unknown> = {}) =>
    request<BupotReceived[]>('GET', `/api/bupot/received${qs(params)}`),
  createBupotReceived: (body: unknown) =>
    request<BupotReceived>('POST', '/api/bupot/received', body),
  deleteBupotReceived: (id: string) => request<void>('DELETE', `/api/bupot/received/${id}`),
  listBupotIssued: (params: Record<string, unknown> = {}) =>
    request<BupotIssued[]>('GET', `/api/bupot/issued${qs(params)}`),
  updateBupotIssued: (id: string, body: unknown) =>
    request<BupotIssued>('PATCH', `/api/bupot/issued/${id}`, body),
  listTaxPayments: (params: Record<string, unknown> = {}) =>
    request<TaxPayment[]>('GET', `/api/tax-payments${qs(params)}`),
  createTaxPayment: (body: unknown) => request<TaxPayment>('POST', '/api/tax-payments', body),
  markTaxPaymentPaid: (id: string, body: { paidAt: string; ntpn: string }) =>
    request<TaxPayment>('POST', `/api/tax-payments/${id}/mark-paid`, body),

  // ── Finance epic: PPh 21 salary records ──
  listSalaryRecords: (params: Record<string, unknown> = {}) =>
    request<SalaryRecord[]>('GET', `/api/payroll-tax/records${qs(params)}`),
  createSalaryRecord: (body: unknown) =>
    request<SalaryRecord>('POST', '/api/payroll-tax/records', body),
  deleteSalaryRecord: (id: string) => request<void>('DELETE', `/api/payroll-tax/records/${id}`),
  markSalaryPaid: (id: string, body: { paidAt: string }) =>
    request<SalaryRecord>('POST', `/api/payroll-tax/records/${id}/mark-paid`, body),

  // ── Finance epic: vendors + withheld payments ──
  listVendors: () => request<Vendor[]>('GET', '/api/vendors'),
  createVendor: (body: unknown) => request<Vendor>('POST', '/api/vendors', body),
  updateVendor: (id: string, body: unknown) =>
    request<Vendor>('PATCH', `/api/vendors/${id}`, body),
  listVendorPayments: (status?: string) =>
    request<VendorPayment[]>('GET', `/api/vendor-payments${qs({ status })}`),
  createVendorPayment: (body: unknown) =>
    request<VendorPayment>('POST', '/api/vendor-payments', body),
  approveVendorPayment: (id: string) =>
    request<VendorPayment>('POST', `/api/vendor-payments/${id}/approve`),
  markVendorPaymentPaid: (id: string, body: { paidAt: string }) =>
    request<VendorPayment>('POST', `/api/vendor-payments/${id}/mark-paid`, body),

  // ── HR: people / contracts / leave (P4) ──
  listPeople: (params: Record<string, unknown> = {}) =>
    request<Person[]>('GET', `/api/people${qs(params)}`),
  getPerson: (id: string) => request<Person>('GET', `/api/people/${id}`),
  createPerson: (body: Partial<Person> | Record<string, unknown>) =>
    request<Person>('POST', '/api/people', body),
  updatePerson: (id: string, body: Partial<Person> | Record<string, unknown>) =>
    request<Person>('PATCH', `/api/people/${id}`, body),
  deactivatePerson: (id: string) => request<Person>('DELETE', `/api/people/${id}`),
  expiringContracts: () => request<ExpiryReminder[]>('GET', '/api/people/expiring'),
  listContracts: (personId: string) =>
    request<EmploymentContract[]>('GET', `/api/people/${personId}/contracts`),
  addContract: (personId: string, body: Partial<EmploymentContract>) =>
    request<EmploymentContract>('POST', `/api/people/${personId}/contracts`, body),

  listLeave: (params: Record<string, unknown> = {}) =>
    request<LeaveRequest[]>('GET', `/api/leave${qs(params)}`),
  createLeave: (body: unknown) => request<LeaveRequest>('POST', '/api/leave', body),
  decideLeave: (
    id: string,
    body: { decision: 'approved' | 'rejected'; note?: string; allowOverbalance?: boolean },
  ) => request<LeaveRequest>('POST', `/api/leave/${id}/decide`, body),
  leaveBalances: (year?: number) =>
    request<LeaveBalanceView[]>('GET', `/api/leave/balances${qs({ year })}`),
  setLeaveBalance: (body: { personId: string; year: number; entitled: number }) =>
    request<LeaveBalanceView>('POST', '/api/leave/balances', body),

  // ── HR: payroll export (P4) ──
  listPayrollExports: () => request<PayrollExport[]>('GET', '/api/payroll/exports'),
  payrollPreview: () => request<PayrollPreview>('GET', '/api/payroll/exports/preview'),
  generatePayrollExport: (body: { period: string; provider?: PayrollProvider }) =>
    request<PayrollExport>('POST', '/api/payroll/exports', body),

  // ── Finance epic: labor allocation + project P&L (P5) ──
  projectPnl: (projectId: string) =>
    request<ProjectPnl>('GET', `/api/projects/${projectId}/pnl`),
  laborAllocations: (month: string) =>
    request<MonthAllocations>('GET', `/api/labor-allocations${qs({ month })}`),
  closeLaborMonth: (month: string) =>
    request<MonthAllocations>('POST', '/api/labor-allocations/close', { month }),
  setManualAllocation: (body: {
    personId: string;
    month: string;
    rows: Array<{ projectId?: string | null; days?: number; amount: number }>;
  }) => request<MonthAllocations>('PUT', '/api/labor-allocations/manual', body),

  // ── Finance epic: budgets ──
  listBudgets: () => request<Budget[]>('GET', '/api/budgets'),
  budgetStatus: (period?: string) =>
    request<BudgetStatusRow[]>('GET', `/api/budgets/status${qs({ period })}`),
  createBudget: (body: unknown) => request<Budget>('POST', '/api/budgets', body),
  updateBudget: (id: string, body: unknown) =>
    request<Budget>('PATCH', `/api/budgets/${id}`, body),
  deleteBudget: (id: string) => request<void>('DELETE', `/api/budgets/${id}`),
};
